import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useReactToPrint } from 'react-to-print';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import {
  Hotel,
  Search,
  CalendarToday,
  Print,
  EventNote
} from '@mui/icons-material';
import { ipdAPI, patientAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import IPDAdmissionForms from '../IPDAdmissionForms';

function IPDPatientsList() {
  const navigate = useNavigate();
  const [admissions, setAdmissions] = useState([]);
  const [filteredAdmissions, setFilteredAdmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [formsPatient, setFormsPatient] = useState(null);
  const [dailyPlanDialog, setDailyPlanDialog] = useState({ open: false, admission: null, date: new Date().toISOString().split('T')[0] });
  const [dailyPlanData, setDailyPlanData] = useState(null);
  const [dailyPlanLoading, setDailyPlanLoading] = useState(false);
  const dailyPlanPrintRef = useRef();
  const handlePrintDailyPlan = useReactToPrint({
    contentRef: dailyPlanPrintRef,
    documentTitle: 'Patient Daily Plan',
    pageStyle: '@page { size: A4 portrait; margin: 10mm; }'
  });

  const openDailyPlan = (admission) => {
    setDailyPlanData(null);
    setDailyPlanDialog({ open: true, admission, date: new Date().toISOString().split('T')[0] });
  };

  const fetchAndPrintDailyPlan = async () => {
    setDailyPlanLoading(true);
    try {
      const res = await patientAPI.getDailyPlan(dailyPlanDialog.admission.patient_id, dailyPlanDialog.date);
      setDailyPlanData(res.data.data);
      setTimeout(() => handlePrintDailyPlan(), 300);
    } catch (err) {
      console.error('Failed to load daily plan', err);
    } finally {
      setDailyPlanLoading(false);
    }
  };

  useEffect(() => {
    loadActiveAdmissions();
  }, []);

  useEffect(() => {
    filterAdmissions();
  }, [searchQuery, admissions]);

  const loadActiveAdmissions = async () => {
    setLoading(true);
    try {
      const response = await ipdAPI.getActiveAdmissions();
      const data = response.data.data || response.data;
      setAdmissions(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load IPD patients. Please try again.');
      console.error('Load admissions error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterAdmissions = () => {
    if (!searchQuery.trim()) {
      setFilteredAdmissions(admissions);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = admissions.filter(admission =>
      admission.patient_code?.toLowerCase().includes(query) ||
      admission.first_name?.toLowerCase().includes(query) ||
      admission.last_name?.toLowerCase().includes(query) ||
      admission.room_number?.toString().includes(query) ||
      admission.category_name?.toLowerCase().includes(query)
    );
    setFilteredAdmissions(filtered);
  };

  const getCategoryColor = (category) => {
    switch (category?.toLowerCase()) {
      case 'hotel': return 'success';
      case 'cottage': return 'primary';
      case 'ward': return 'info';
      default: return 'default';
    }
  };

  const handleManagePatient = (admission) => {
    navigate(`/doctor/ipd-patient/${admission.id}`, {
      state: {
        patientName: `${admission.first_name} ${admission.last_name}`,
        patientCode: admission.patient_code,
        patientId: admission.patient_id,
        roomNumber: admission.room_number,
        admissionDate: admission.admission_date,
        plannedDays: admission.planned_days
      }
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card elevation={2}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Hotel sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Typography variant="h5" component="h2" fontWeight="bold">
            IPD Patients
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          placeholder="Search by patient code, name, or room number"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 3 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />

        {filteredAdmissions.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Typography color="text.secondary">
              {searchQuery ? 'No patients found matching your search' : 'No active IPD patients'}
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><strong>Patient Code</strong></TableCell>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Room</strong></TableCell>
                  <TableCell><strong>Category</strong></TableCell>
                  <TableCell><strong>Admission Date</strong></TableCell>
                  <TableCell align="right"><strong>Days Admitted</strong></TableCell>
                  <TableCell align="right"><strong>Planned Days</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAdmissions.map((admission) => (
                  <TableRow key={admission.id} hover>
                    <TableCell>{admission.patient_code}</TableCell>
                    <TableCell>{admission.first_name} {admission.last_name}</TableCell>
                    <TableCell>{admission.room_number}</TableCell>
                    <TableCell>
                      <Chip
                        label={admission.category_name}
                        color={getCategoryColor(admission.category_name)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(admission.admission_date).toLocaleDateString('en-GB')}
                    </TableCell>
                    <TableCell align="right">{admission.days_admitted}</TableCell>
                    <TableCell align="right">{admission.planned_days || 0}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="Print Daily Treatment & Diet Plan">
                          <Button variant="outlined" size="small" color="secondary" startIcon={<EventNote />}
                            onClick={() => openDailyPlan(admission)}>
                            Daily Plan
                          </Button>
                        </Tooltip>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Print />}
                          onClick={() => setFormsPatient({
                            firstName: admission.first_name,
                            lastName: admission.last_name,
                            patientCode: admission.patient_code,
                            phone: admission.phone,
                            gender: admission.gender,
                            address: admission.address,
                            fatherHusbandName: admission.father_husband_name,
                            father_husband_name: admission.father_husband_name,
                            admissionData: {
                              admissionId: admission.id,
                              roomNumber: admission.room_number,
                              admissionDate: admission.admission_date,
                              diagnosis: admission.diagnosis || '',
                              chiefComplaint: admission.chief_complaint || '',
                              pastMedicalHistory: admission.past_medical_history || '',
                              currentMedications: admission.current_medications || '',
                              familyHistory: admission.family_history || '',
                              doctorNotes: admission.doctor_notes || '',
                            }
                          })}
                        >
                          Forms
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleManagePatient(admission)}
                          sx={{ minWidth: 110 }}
                        >
                          Manage Patient
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
          <CalendarToday fontSize="small" />
          <Typography variant="body2">
            Total Active Patients: {filteredAdmissions.length}
          </Typography>
        </Box>
      </CardContent>

      {formsPatient && (
        <IPDAdmissionForms
          patient={formsPatient}
          admission={formsPatient.admissionData}
          onClose={() => setFormsPatient(null)}
        />
      )}

      {/* Daily Plan dialog */}
      <Dialog open={dailyPlanDialog.open} onClose={() => setDailyPlanDialog(d => ({ ...d, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle>Print Daily Treatment & Diet Plan</DialogTitle>
        <DialogContent>
          {dailyPlanDialog.admission && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Patient: <strong>{dailyPlanDialog.admission.first_name} {dailyPlanDialog.admission.last_name}</strong> — Room {dailyPlanDialog.admission.room_number}
            </Typography>
          )}
          <TextField
            fullWidth label="Date" type="date"
            value={dailyPlanDialog.date}
            onChange={e => setDailyPlanDialog(d => ({ ...d, date: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDailyPlanDialog(d => ({ ...d, open: false }))}>Cancel</Button>
          <Button variant="contained" startIcon={dailyPlanLoading ? <CircularProgress size={16} color="inherit" /> : <Print />}
            onClick={fetchAndPrintDailyPlan} disabled={dailyPlanLoading}>
            {dailyPlanLoading ? 'Loading...' : 'Print'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Daily plan print portal */}
      {dailyPlanData && createPortal(
        <div style={{ position: 'fixed', top: 0, left: '-9999px', width: '190mm' }}>
          <div ref={dailyPlanPrintRef} style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#111', background: 'white', padding: '8mm 10mm' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', borderBottom: '2px solid #1b5e20', paddingBottom: 8, marginBottom: 10 }}>
              <img src="/logo.png" alt="MSG Logo" style={{ width: 48, height: 48, objectFit: 'contain', flexShrink: 0 }} />
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 900, color: '#1b5e20' }}>MSG NATUROPATHY CENTER</div>
                <div style={{ fontSize: 9, color: '#444' }}>Sirsa, Haryana &nbsp;|&nbsp; +91 7082999551 / 7082999351</div>
                <div style={{ fontSize: 12, fontWeight: 700, marginTop: 3 }}>PATIENT DAILY PLAN</div>
              </div>
            </div>

            {/* Patient info */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 16px', marginBottom: 10, fontSize: 10 }}>
              <div><strong>Patient:</strong> {dailyPlanData.patient.first_name} {dailyPlanData.patient.last_name}</div>
              <div><strong>UHID:</strong> {dailyPlanData.patient.patient_code}</div>
              <div><strong>Room:</strong> {dailyPlanData.admission?.room_number} ({dailyPlanData.admission?.category_name})</div>
              <div><strong>Date:</strong> {new Date(dailyPlanDialog.date).toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}</div>
            </div>

            {/* Vitals */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ background: '#fce4ec', padding: '4px 8px', fontWeight: 700, fontSize: 12, borderLeft: '4px solid #c62828', marginBottom: 4 }}>
                Vitals ({(dailyPlanData.vitals || []).length} {(dailyPlanData.vitals || []).length === 1 ? 'record' : 'records'})
              </div>
              {!(dailyPlanData.vitals || []).length ? (
                <div style={{ color: '#888', fontSize: 10, padding: '6px 8px' }}>No vitals recorded for this date.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      {['Time', 'BP', 'Temp (°F)', 'Pulse', 'RR', 'SpO2%', 'Wt (kg)', 'RBS', 'Insulin (U)', 'Pain'].map(h => (
                        <th key={h} style={{ border: '1px solid #ddd', padding: '3px 5px', textAlign: 'center' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(dailyPlanData.vitals || []).map((v, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa', textAlign: 'center' }}>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{new Date(v.recorded_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</td>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{v.blood_pressure || '—'}</td>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{v.temperature || '—'}</td>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{v.pulse_rate || '—'}</td>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{v.respiratory_rate || '—'}</td>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{v.oxygen_saturation || '—'}</td>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{v.weight || '—'}</td>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{v.rbs || '—'}</td>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px' }}>{v.insulin_units || '—'}</td>
                        <td style={{ border: '1px solid #ddd', padding: '3px 5px', fontWeight: 600 }}>{v.pain_scale ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Treatments */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ background: '#e8f5e9', padding: '4px 8px', fontWeight: 700, fontSize: 12, borderLeft: '4px solid #2e7d32', marginBottom: 4 }}>
                Treatment Plan ({[...new Set(dailyPlanData.treatments.map(t => t.time_slot))].length} slots, {dailyPlanData.treatments.length} items)
              </div>
              {dailyPlanData.treatments.length === 0 ? (
                <div style={{ color: '#888', fontSize: 10, padding: '6px 8px' }}>No treatments scheduled for this date.</div>
              ) : (() => {
                const slotOrder = ['Satkriya', 'Morning', 'Evening', 'Night'];
                const grouped = {};
                dailyPlanData.treatments.forEach(t => {
                  const slot = t.time_slot || 'Other';
                  if (!grouped[slot]) grouped[slot] = [];
                  grouped[slot].push(t);
                });
                const slots = [...slotOrder.filter(s => grouped[s]), ...Object.keys(grouped).filter(s => !slotOrder.includes(s))];
                return (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                    <thead>
                      <tr style={{ background: '#f5f5f5' }}>
                        {['Time Slot', 'Treatments', 'Assigned To'].map(h => (
                          <th key={h} style={{ border: '1px solid #ddd', padding: '3px 6px', textAlign: 'left' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {slots.map((slot, i) => {
                        const items = grouped[slot];
                        return (
                          <tr key={slot} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td style={{ border: '1px solid #ddd', padding: '3px 6px', whiteSpace: 'nowrap' }}>{slot}</td>
                            <td style={{ border: '1px solid #ddd', padding: '3px 6px', fontWeight: 600 }}>
                              {items.map(t => t.treatment_name).join(', ')}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '3px 6px' }}>
                              {[...new Set(items.map(t => t.assigned_to_name).filter(Boolean))].join(', ') || '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
              })()}
            </div>

            {/* Diet */}
            <div>
              <div style={{ background: '#fff8e1', padding: '4px 8px', fontWeight: 700, fontSize: 12, borderLeft: '4px solid #f57f17', marginBottom: 4 }}>
                Diet Plan ({[...new Set(dailyPlanData.meals.map(m => m.meal_time))].length} meal times, {dailyPlanData.meals.length} items)
              </div>
              {dailyPlanData.meals.length === 0 ? (
                <div style={{ color: '#888', fontSize: 10, padding: '6px 8px' }}>No diet plan scheduled for this date.</div>
              ) : (() => {
                const mealOrder = ['7AM', 'Breakfast', '11AM', 'Lunch', '5PM', 'Dinner'];
                const grouped = {};
                dailyPlanData.meals.forEach(m => {
                  const meal = m.meal_time || 'Other';
                  if (!grouped[meal]) grouped[meal] = [];
                  grouped[meal].push(m);
                });
                const meals = [...mealOrder.filter(m => grouped[m]), ...Object.keys(grouped).filter(m => !mealOrder.includes(m))];
                return (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                    <thead>
                      <tr style={{ background: '#f5f5f5' }}>
                        {['Meal Time', 'Diet Items', 'Instructions'].map(h => (
                          <th key={h} style={{ border: '1px solid #ddd', padding: '3px 6px', textAlign: 'left' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {meals.map((meal, i) => {
                        const items = grouped[meal];
                        return (
                          <tr key={meal} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td style={{ border: '1px solid #ddd', padding: '3px 6px', whiteSpace: 'nowrap' }}>{meal}</td>
                            <td style={{ border: '1px solid #ddd', padding: '3px 6px', fontWeight: 600 }}>
                              {items.map(m => m.quantity ? `${m.diet_item_name} (${m.quantity})` : m.diet_item_name).join(', ')}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '3px 6px', color: '#555' }}>
                              {items.map(m => m.special_instructions).filter(Boolean).join('; ') || '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
              })()}
            </div>

            <div style={{ marginTop: 16, borderTop: '1px solid #eee', paddingTop: 6, textAlign: 'center', fontSize: 9, color: '#888' }}>
              MSG Elite One World Wellness — Printed: {new Date().toLocaleString('en-IN')}
            </div>
          </div>
        </div>,
        document.body
      )}
    </Card>
  );
}

export default IPDPatientsList;
